import { StaticImageData } from 'next/image';

import { BlogCategory } from '@/../marketing/data/blog-categories';

import faultSimImage from './blog-posts/images/fault-sim.png';
import resourceproImage from './blog-posts/images/resourcepro.png';
import batterySimulationsImage from './blog-posts/battery-simulations.gif';
import quickfixValidationsImage from './blog-posts/quickfix-validations.gif';
import powerFlowSimulationsImage from './blog-posts/power-flow-simulations.gif';

export type BlogPost = {
    slug?: string;
    href?: string;
    title: string;
    description: string;
    category: BlogCategory;
    date: string;
    author: string;
    isVideo?: boolean;
    image?: StaticImageData | string;
};

const blogPosts: BlogPost[] = [
    {
        slug: 'fault-simulations',
        title: 'DC Fault Simulations in DCIDE: Design Protection With Confidence',
        description:
            "Prove your DC protection works before anything gets built. Run DC short-circuit fault simulations online, with DEP's DC engineers backing you every step of the way.",
        category: BlogCategory.UPDATES,
        date: '21 May 2026',
        author: 'Direct Energy Partners',
        image: faultSimImage,
    },
    {
        href: 'https://www.youtube.com/watch?v=ZVgxERVex2A',
        title: 'Webinar: Master DC Wire Sizing',
        description:
            'Discover how to automatically size DC cables in accordance with IEC and NEC standards in this expert-led webinar presented by Fraunhofer IPA.',
        category: BlogCategory.TUTORIAL,
        date: '27 November 2025',
        author: 'Direct Energy Partners',
        isVideo: true,
        image: '/images/wire-sizing-webinar.jpg',
    },
    {
        slug: 'unbalanced-ac-dc-power-flow',
        title: 'Unbalanced AC/DC Power Flow',
        description:
            'Simulate unbalanced three-phase AC systems and mixed AC/DC microgrids with per-phase phasor modeling, neutral current analysis, and single-phase AC/DC converter support.',
        category: BlogCategory.UPDATES,
        date: '31 March 2026',
        author: 'Direct Energy Partners',
        image: '/images/unbalanced-ac-dc-power-flow.gif',
    },
    {
        slug: 'dc-experts-at-your-fingertips',
        title: 'DC Experts. At Your Fingertips.',
        description:
            'DC Expert support connects you directly with engineers who have designed, simulated, and commissioned real DC power systems. Get answers from people who can look at your diagram and tell you what will happen.',
        category: BlogCategory.NEWS,
        date: '30 March 2026',
        author: 'Direct Energy Partners',
        image: '/images/engineering-gif.gif',
    },
    {
        slug: 'dc-wire-sizing',
        title: 'DC Wire Sizing',
        description:
            'Automatically size DC cables with worst-case simulations and validate against IEC and NEC standards for current carrying capability, voltage drop, and overcurrent protection.',
        category: BlogCategory.UPDATES,
        date: '3 November 2025',
        author: 'Direct Energy Partners',
        image: '/images/blogs/wire-sizing.png',
    },
    {
        slug: 'electric-vehicle-ev-pv-solar-droop-control-curtailment-simulation',
        title: 'Mastering Droop Control: Electric Vehicle & Solar Curtailment Simulation',
        description:
            "Discover how to design stable DC Microgrids with real-time droop control for EV chargers and solar PV systems. Learn to implement voltage-based power sharing and automatic solar curtailment in minutes using DCIDE's intuitive simulation platform.",
        category: BlogCategory.TUTORIAL,
        date: '11 June 2025',
        author: 'Direct Energy Partners',
        isVideo: true,
        image: '/images/blogs/PV_EV_SOLAR_CURTAILMENT/DCIDE_PV_EV_SOLAR_DROOP.png',
    },
    {
        slug: 'simulation-profiles',
        title: 'Simulation Profiles',
        description: 'Unlock Dynamic Insights with Simulation Profiles in DCIDE!',
        category: BlogCategory.UPDATES,
        date: '10 June 2025',
        author: 'Direct Energy Partners',
        image: '/images/blogs/simulation-profiles.png',
    },
    {
        slug: 'simulate-power-conversion-losses',
        title: 'Simulate power conversion Losses',
        description:
            'Configure your converter efficiency curves and simulate power conversion losses in your microgrid designs.',
        category: BlogCategory.UPDATES,
        date: '14 February 2025',
        author: 'Direct Energy Partners',
        image: '/images/simulate-power-conversion-losses.gif',
    },
    {
        slug: 'dep-and-resourcepro',
        title: 'DEP and RE+ Events introduce RE+Source PRO',
        description:
            'RE+Source PRO is a new online platform that streamlines microgrid product discovery and concept design by connecting designers and engineers with leading manufacturers.',
        category: BlogCategory.NEWS,
        date: '11 January 2025',
        author: 'Direct Energy Partners',
        image: resourceproImage,
    },
    {
        slug: 'battery-simulations',
        title: 'Simulate battery (dis)charging',
        description:
            'Upload real-world load and generation data and specify battery capacity to accurately model charging and discharging cycles.',
        category: BlogCategory.UPDATES,
        date: '25 November 2024',
        author: 'Direct Energy Partners',
        image: batterySimulationsImage,
    },
    {
        slug: 'quickfix-validations',
        title: 'Quickfix validations',
        description: 'Validate & fix your microgrid designs against industry standards and best practices.',
        category: BlogCategory.UPDATES,
        date: '12 October 2024',
        author: 'Direct Energy Partners',
        image: quickfixValidationsImage,
    },
    {
        href: 'https://www.youtube.com/watch?v=rcT_kFpyl1k',
        title: 'Share reference designs in DCIDE',
        description:
            'Learn how to create and share a microgrid reference design, including adding components, your logo, and annotations',
        category: BlogCategory.TUTORIAL,
        isVideo: true,
        date: '26 September 2024',
        author: 'Direct Energy Partners',
        image: 'https://img.youtube.com/vi/rcT_kFpyl1k/maxresdefault.jpg',
    },
    {
        href: 'https://www.youtube.com/watch?v=_xpgcHbKvCo',
        title: 'Power flow simulations in DCIDE',
        description:
            'This video demonstrates power flow simulations in DCIDE, showcasing a 700V, 35kW system with battery-to-EV charger connection and power-voltage droop control.',
        category: BlogCategory.TUTORIAL,
        isVideo: true,
        date: '25 July 2024',
        author: 'Direct Energy Partners',
        image: 'https://img.youtube.com/vi/_xpgcHbKvCo/maxresdefault.jpg',
    },
    {
        href: 'https://www.youtube.com/watch?v=NzKfnJyyXLc',
        title: 'Schneider Electric / CurrentOS',
        description:
            'In this insightful webinar, Avinash Papisetti and Chris Evanich share their expertise on the latest advancements in the energy sector.',
        category: BlogCategory.TUTORIAL,
        isVideo: true,
        date: '13 June 2024',
        author: 'Schneider Electric',
        image: 'https://img.youtube.com/vi/NzKfnJyyXLc/maxresdefault.jpg',
    },
    {
        slug: 'power-flow-simulations',
        title: 'Power flow simulations',
        description: 'Visualize power, voltages and currents and experience how your designs could work in practice.',
        category: BlogCategory.UPDATES,
        date: '7 June 2024',
        author: 'Direct Energy Partners',
        image: powerFlowSimulationsImage,
    },
    {
        href: 'https://www.youtube.com/watch?v=CPpANtCUA7A',
        title: 'Design with CE+T / Stabiliti 30C3',
        description:
            'Mario Barbaresso (President and CEO of CE+T) shares his insights on how to design a hybrid AC/DC Microgrid with the Stabiliti 30C3.',
        category: BlogCategory.TUTORIAL,
        isVideo: true,
        date: '13 May 2024',
        author: 'CE+T & DCIDE',
        image: 'https://img.youtube.com/vi/CPpANtCUA7A/maxresdefault.jpg',
    },
    {
        href: 'https://new.abb.com/news/detail/100631/abb-invests-in-direct-current-microgrid-start-up-direct-energy-partners-to-accelerate-energy-transformation',
        title: 'ABB invests in Direct Energy Partners',
        description:
            'ABB is investing in Direct Energy Partners, a start-up that is accelerating the adoption of DC Microgrids through its innovative software platform, DCIDE.',
        category: BlogCategory.NEWS,
        date: '3 August 2023',
        author: 'Direct Energy Partners',
    },
];

export { blogPosts };
